import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addItemToLast, pushToVersions, setSectionFormType, setSelectedCarouselBannerSection, updateVersionIndex } from '../../../../../Features/storefrontSlice';
import CrossGray from '../../../../../Assets/StorefrontIcons/CrossGray.svg';
import { StorefrontContext } from '../../../../../Context/StorefrontContext';
import BannerSection from './BannerSection';
import { v4 as uuidv4 } from 'uuid';
import { getCategory, getCollection, getCustomCollection, getProduct } from '../../../../../lib/utils/apiCalls';
import { getMediaUrl } from '../../../../../lib/utils/utils';
import imageCompression from 'browser-image-compression';

function CarouselBannerForm() {
    const dispatch = useDispatch();

    const selectedCarouselBannerSection = useSelector((state) => state.storefront.selectedCarouselBannerSection);
    const versionStack = useSelector((state) => state.storefront.versionStack);
    const versionIndex = useSelector((state) => state.storefront.versionIndex);

    const {
        defaultSingleBanner,
        defaultCarouselBanner,
        carouselBanner,
        setCarouselBanner,
        alert,
        // setAlert,
        // defaultAlert,
        // defaultCarouselAlert,
    } = useContext(StorefrontContext);

    function closeForm() {
        dispatch(setSectionFormType(null));
        dispatch(setSelectedCarouselBannerSection('Banner 1'));
        setCarouselBanner(defaultCarouselBanner);
    }

    async function handleSave() {
        const currentVersion = versionStack[versionIndex];

        const newCarouselData = await Promise.all(carouselBanner
            .filter(banner => banner.image && (banner.image.file || banner.image.url))
            .map(async banner => {
                const object = {};

                object.altText = banner.altText || "";

                if(Object.keys(banner.link.category).length > 0) {
                    object.typeProduct = 'CATEGORY';
                    object.source = banner.link.category.id;
                }
                else if(Object.keys(banner.link.categoryCollection).length > 0) {
                    object.typeProduct = 'CATEGORY_COLLECTION';
                    object.source = banner.link.categoryCollection.id;
                }
                else if(Object.keys(banner.link.customCollection).length > 0) {
                    object.typeProduct = 'CUSTOM_COLLECTION';
                    object.source = banner.link.customCollection.id;
                }
                else if(Object.keys(banner.link.product).length > 0) {
                    object.typeProduct = 'PRODUCT';
                    object.source = banner.link.product.id || banner.link.product.product.id;
                }

                const image = banner.image.file;

                if (image) {
                    let url;
        
                    if (image.size > 1048576) {
                        try {
                            const compressedBlob = await imageCompression(image, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                            const compressedFile = new File([compressedBlob], image.name, { type: image.type, lastModified: Date.now() });

                            url = await getMediaUrl(compressedFile);
                        }
                        catch (err) {
                            console.warn('Image compression failed, using original file:', err);
                            url = await getMediaUrl(image);
                        }
                    }
                    else {
                        url = await getMediaUrl(image);
                    }
                    object.imageLink = url;
                }
                else {
                    object.imageLink = banner.image.url || "";
                }
        
                return { ...object };
            }
        ));

        const tempVersionData = [ ...currentVersion.versionData ];

        const carouselBannerDataIndex = tempVersionData.findIndex(
            item => item.item.typeData === 'CAROUSEL_BANNER'
        );

        if (carouselBannerDataIndex !== -1) {
            tempVersionData[carouselBannerDataIndex] = {
                ...tempVersionData[carouselBannerDataIndex],
                item: {
                    ...tempVersionData[carouselBannerDataIndex].item,
                    imageLinkList: newCarouselData,
                }
            };

            const newIndex = versionStack[versionStack.length - 1].index + 1;

            const newVersion = {
                ...currentVersion,
                index: newIndex,
                versionData: tempVersionData,
                comment: 'update carousel banner',
            }

            dispatch(pushToVersions(newVersion));
            dispatch(updateVersionIndex(newIndex));
        }
        else {
            const object = {
                id: uuidv4(),
                typeData: 'CAROUSEL_BANNER',
                imageLinkList: newCarouselData
            }

            dispatch(addItemToLast(object));
        }
        closeForm();
    }

    useEffect(() => {
        async function fetchData() {
            const tempVersion = versionStack[versionIndex]?.versionData;

            if (tempVersion.length > 0) {
                const { item: carouselData = null } = tempVersion.find(item => item.item.typeData === 'CAROUSEL_BANNER') || {};

                const imageList = carouselData?.imageLinkList ? [...carouselData.imageLinkList] : [];

                if (imageList.length < 5) {
                    for (let i = imageList.length; i < 5; i++) {
                        const object = {
                            altText: '',
                            title: `Banner ${i + 1}`,
                            imageLink: '',
                            source: '',
                            typeProduct: '',
                        };
                
                        imageList.push(object);
                    }
                }

                if (carouselData) {
                    const dataToAddPromises = imageList.map(async (image, index) => {
                        const banner = {};
                        banner.altText = image.altText;
                        banner.image = {
                            id: uuidv4(),
                            url: image.imageLink,
                            file: null,
                        };
                        banner.title = banner.title ? banner.title : `Banner ${index + 1}`;

                        try {
                            if (image.typeProduct === 'CATEGORY_COLLECTION') {
                                const response = await getCollection(image.source);
                                if (response.status === 200) {
                                    banner.link = { ...defaultSingleBanner, categoryCollection: response.data.data.collection };
                                }
                            }
                            else if (image.typeProduct === 'CATEGORY') {
                                const response = await getCategory(image.source);
                                if (response.status === 200) {
                                    banner.link = { ...defaultSingleBanner, category: response.data.data.category };
                                }
                            }
                            else if (image.typeProduct === 'CUSTOM_COLLECTION') {
                                const response = await getCustomCollection(image.source);
                                if (response.status === 200) {
                                    banner.link = { ...defaultSingleBanner, customCollection: response.data.data.collection };
                                }
                            }
                            else if (image.typeProduct === 'PRODUCT') {
                                const response = await getProduct(image.source);

                                if (response.status === 200) {
                                    banner.link = { ...defaultSingleBanner, product: response.data.data };
                                }
                            }
                            else {
                                banner.link = { ...defaultSingleBanner };
                            }
                        }
                        catch (err) {
                            console.log(err);
                        }

                        return banner;
                    });

                    const dataToAdd = await Promise.all(dataToAddPromises);
                    setCarouselBanner(dataToAdd);
                }
            }
            else {
                setCarouselBanner(defaultCarouselBanner);
            }
        };

        fetchData();
        // eslint-disable-next-line
    }, [versionStack, versionIndex]);

    return (
        <div className='w-[305px] h-full bg-[#FFFFFF] border-l border-[#EAEAEA] flex flex-col gap-4 select-none relative'>
            <div className="flex items-center justify-between">
                <p className='font-semibold text-[20px] leading-6 text-[#121212] pl-3'>Carousel Banner</p>

                <div className="size-11 p-[10px] flex items-center justify-center">
                    <button onClick={closeForm} className='size-6 flex items-center justify-center outline-none'>
                        <img src={CrossGray} alt="" />
                    </button>
                </div>
            </div>

            <div className="h-[calc(100%-44px)] flex flex-col overflow-y-scroll no-scrollbar pb-16">
                {
                    carouselBanner.map((banner, index) => {
                        const isSelected = selectedCarouselBannerSection === banner.title;
                        const isFirst = index === 0;
                        const bannerAlertEntry = alert.carouselBanner.find(item => item.index === index + 1);
                        const active = bannerAlertEntry?.bannerAvailable || false;
                        const alertAvailable = bannerAlertEntry?.bannerAlert || false;

                        return (
                            <BannerSection 
                                key={banner.title} 
                                banner={banner} 
                                isSelected={isSelected} 
                                isFirst={isFirst} 
                                isActive={active} 
                                hasAlert={alertAvailable} 
                            />
                        )
                    })
                }
            </div>

            <div className="absolute bottom-0 left-0 w-full p-3 bg-[#FFFFFF]">
                <button onClick={handleSave} className='w-full outline-none px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent rounded-md font-semibold text-[12px] leading-6 text-buttonText'>Save changes</button>
            </div>
        </div>
    );
}

export default CarouselBannerForm;
